import { useCallback, useEffect, useRef } from 'react';

import { HOMEPAGE_VIDEO_TIMEOUT_IN_MILLIS } from '@web-platform/shared-universal-constants/src/adverts';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type { VideoTrackingDeps, VideoTrackingHandlers } from './videoTracking.types';

// `onEnded` never fires under `loop` (native player behaviour), so completion is inferred
// from a near-end playhead milestone instead.
const AD_COMPLETED_PLAYHEAD_MILESTONE = 0.95;
const QUARTILE_PLAYHEAD_MILESTONES = [0.25, 0.5, 0.75];

export const PLAYHEAD_MILESTONES = [
  ...QUARTILE_PLAYHEAD_MILESTONES,
  AD_COMPLETED_PLAYHEAD_MILESTONE,
];

type Deps = Omit<VideoTrackingDeps, 'getVideo'>;

/**
 * Spicy `VideoPlayer`'s tracking. `AD_STOPPED` comes only from a genuine pause (the
 * player's own `onPause`), never redirect/close-fullscreen, and has no `watchDuration`.
 * Quartile/`AD_COMPLETED` events derive from playhead milestones and fire regardless of
 * fullscreen state, unlike legacy's fullscreen-gated `AD_COMPLETED`.
 *
 * @returns {VideoTrackingHandlers} handlers to wire onto the Spicy `VideoPlayer` and its surrounding component
 */
export const useSpicyVideoTracking = ({
  creativeId,
  isFullScreen,
}: Deps): VideoTrackingHandlers => {
  const hasCompletedRef = useRef(false);
  // The native `pause` event fires as a separate, deferred browser task. onExitFullScreen's
  // prefersReducedMotion path pauses synchronously and only afterwards clears fullscreen
  // state — by the time the deferred pause event lands, that state clear has already
  // flushed, so reading isFullScreen live would tag the event 'collapsed' even though the
  // pause was initiated while still in fullscreen. Captured at the point pause() is
  // actually called (notePauseInitiated), not read live, so it survives the state flush.
  const pauseInitiatedInFullScreenRef = useRef(isFullScreen);
  const hasShownFallbackRef = useRef(false);
  const readyTimedOutRef = useRef(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trackShowingFallbackContent = useCallback(() => {
    if (hasShownFallbackRef.current) {
      return;
    }
    hasShownFallbackRef.current = true;
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FALLBACK_SHOWN.NAME,
      strings: {
        creativeId,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FALLBACK_SHOWN.NAME,
      lineItemId: creativeId,
    });
  }, [creativeId]);

  const startFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
    readyTimedOutRef.current = false;
    fallbackTimerRef.current = setTimeout(() => {
      readyTimedOutRef.current = true;
      trackShowingFallbackContent();
    }, HOMEPAGE_VIDEO_TIMEOUT_IN_MILLIS);
  }, [trackShowingFallbackContent]);

  useEffect(() => {
    startFallbackTimer();

    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
    };
  }, [creativeId, startFallbackTimer]);

  const onPlayerPause = useCallback(() => {
    const videoViewType = pauseInitiatedInFullScreenRef.current ? 'expanded' : 'collapsed';

    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_STOPPED.NAME,
      strings: {
        creativeId,
        action: 'pause',
        videoViewType,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_STOPPED.NAME,
      lineItemId: creativeId,
      action: 'pause',
      videoViewType,
    });
  }, [creativeId]);

  const onPlayerTimeUpdate = useCallback(
    (milestone: number, _currentTime: number, _duration: number) => {
      if (QUARTILE_PLAYHEAD_MILESTONES.includes(milestone)) {
        const milestonePercent = milestone * 100;
        trackInteractionEvent({
          name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_MILESTONE.NAME,
          strings: { creativeId },
          numbers: { milestonePercent },
        });
        logOperationalEvent({
          eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_MILESTONE.NAME,
          lineItemId: creativeId,
          milestonePercent,
        });
        return;
      }

      if ([AD_COMPLETED_PLAYHEAD_MILESTONE].includes(milestone) && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        trackInteractionEvent({
          name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_COMPLETED.NAME,
          strings: { creativeId },
        });
        logOperationalEvent({
          eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_COMPLETED.NAME,
          lineItemId: creativeId,
        });
      }
    },
    [creativeId],
  );

  const onPlayerReady = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
  }, []);

  const onPlayerError = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
    readyTimedOutRef.current = true;
    trackShowingFallbackContent();
  }, [trackShowingFallbackContent]);

  const didTimeOut = useCallback(() => readyTimedOutRef.current, []);

  return {
    notePauseInitiated: (isFullScreenAtPause: boolean) => {
      pauseInitiatedInFullScreenRef.current = isFullScreenAtPause;
    },
    onEnterFullscreen: () => {},
    onPlayerPause,
    onPlayerTimeUpdate,
    onPlayerReady,
    onPlayerError,
    onPlayerRetry: startFallbackTimer,
    didTimeOut,
  };
};
